// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { FormattedMessage } from 'react-intl';
import RadioButton from '../../../../../elements/RadioButton/RadioButton';
import Checkbox from '../../../../../elements/Checkbox/Checkbox';
import { getQempoAddress } from '../../../../../utils/qempoAdresses';
import Select from '../../../../../elements/Select/Select';
import update from 'immutability-helper';
import UploadBox from '../../components/UploadBox/UploadBox';
import analytics from '../../../../../analytics';
import ReportModalContainer from '../../../../report/container/ReportModalContainer';
import FloatingBottom from '../../../../../elements/FloatingBottom/FloatingBottom';

import commonStyle from '../../../components/OrderCommon.scss';
import boxStyle from '../../../../../assets/styles/box.scss';
import style from './Deliver.scss';
import formStyle from '../../../../../assets/styles/form.scss';
import inputStyle from '../../../../../assets/styles/input.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';

import type { Node } from 'react';
import type { Props as ProductProps } from '../../../../../elements/Product/Product';
import type { Address, Courier, Order } from '../../../../../entities';
import type { LocaleState } from '../../../../locale/duck';
import type { AddressIntl } from '../../../../../utils/qempoAdresses';
import type { Option } from '../../../../../elements/Select/Select';
import type { LocalValuesState } from '../../../../local-values/duck';
import type { Motive, MotiveKey } from '../../../../report/duck';

type Props = {
  +buildProducts: (renderContent?: (props: ProductProps) => Node) => Node;
  +buildPurchaserInfo: () => Node;
  +buildSummary: () => Node;
  +order: Order;
  +locale: LocaleState;
  +localValues: LocalValuesState;
  +postProgressCourier: (orderId: string, courier: string, trackingNumber: string, invoiceURI: string) => void;
  +postProgressQempo: (orderId: string) => void;
  +fetchCouriers: (country: string) => void;
  +couriers?: Courier[];
  +motives?: { [key: MotiveKey]: Motive[] };
};

type State = {
  deliveryMethod: {
    value: string;
    errors?: string[];
  };
  nextStep?: boolean;
  confirmQempoDelivered: boolean;
  courier: {
    value: string;
  };
  trackingNumber: {
    value: string;
  };
  invoice: {
    valueURI: string;
    error?: boolean;
  };
  reportModalVisible: boolean;
};

const validFormats = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];

export default class Deliver extends Component<Props, State> {
  state = {
    deliveryMethod: {
      value: 'qempo',
    },
    nextStep: true,
    confirmQempoDelivered: false,
    courier: {
      value: '',
    },
    trackingNumber: {
      value: '',
    },
    invoice: {
      valueURI: '',
    },
    reportModalVisible: false,
  };

  componentDidMount() {
    const { fetchCouriers, order } = this.props;
    fetchCouriers(order.deliveryCountry.code);
    if (order.deliveryCountry.code !== 'PE') {
      this.setState({
        deliveryMethod: {
          value: 'courier',
        },
      });
    }
  }

  handleChangeTrackingNumber = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.setState({ trackingNumber: { value } });
    }
  };

  handleChangeDeliveryMethod = (e: SyntheticInputEvent<HTMLInputElement>) => {
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.setState({ deliveryMethod: { value } });
    }
  };

  handleChangeConfirm = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      this.setState((prevState: State) => ({
        confirmQempoDelivered: !prevState.confirmQempoDelivered,
      }));
    }
  };

  handleChangeCourier = (option: Option) => {
    if (!(typeof option.value === 'number'))
      return console.log('Option value not number!');
    this.setState({
      courier: {
        value: `${option.value}`,
      }
    });
  };

  handleChangeFile = (acceptedFiles: File[]) => {
    if (acceptedFiles.length) {
      const reader = new FileReader();
      reader.onload = () => {
        const fileAsDataURL = reader.result;
        this.setState({
          invoice: {
            valueURI: fileAsDataURL.toString()
          },
        });
      };
      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');
      reader.readAsDataURL(acceptedFiles[0]);
    }
  };

  handleRejectedFile(rejectedFiles: File[]) {
    if (rejectedFiles.length) {
      if (rejectedFiles[0].type === '' || !validFormats.includes(rejectedFiles[0].type) ||
        rejectedFiles[0].size > 3145728) {
        this.setState((prevState: State) => update(prevState, {
          invoice: {
            error: { $set: true },
          },
        }));
      }
    }
  }

  handleNext = () => {
    const { postProgressQempo, postProgressCourier, order, locale, localValues } = this.props;
    const { nextStep, confirmQempoDelivered, deliveryMethod, courier, invoice, trackingNumber } = this.state;
    if (nextStep) {
      if (deliveryMethod.value === 'qempo' && confirmQempoDelivered) {
        postProgressQempo(order.id);
      } else if (deliveryMethod.value === 'courier') {
        postProgressCourier(order.id, courier.value, trackingNumber.value, invoice.valueURI);
      }
    } else {
      this.setState({ nextStep: true });
      analytics.fireCustomEvent('Delivery Method Selected', {
        category: 'Delivery Method',
        action: 'Selected',
        label: deliveryMethod.value.toLowerCase(),
        user_type: 'traveler',
        flight_id: order.flight.id,
        order_id: order.id,
        order_formatted_id: order.id,
        products: analytics.mapProducts(order.items, localValues.categoriesMap),
        currency: 'USD',
        value: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.total) : 0,
        delivery_method: deliveryMethod.value,
        domain_country: locale.domainCountry,
        subtotal: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.subtotal) : 0,
        commission: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.commission) : 0,
        shipping: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.shipping) : 0,
        tax: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.taxes) : 0,
        discount: order.invoice && order.invoice.detail ? Math.abs(parseFloat(order.invoice.detail.coupon)) : 0,
        coupon: order.couponCode || '',
        // revenue: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.services) : 0,
      });
    }
  };

  handleBack = () => {
    this.setState({
      nextStep: false,
      confirmQempoDelivered: false,
      courier: {
        value: '',
      },
      trackingNumber: {
        value: '',
      },
      invoice: {
        valueURI: '',
      },
    });
  };

  handleCloseReportModal = () => {
    this.setState({ reportModalVisible: false });
  };

  handleOpenReportModal = () => {
    this.setState({ reportModalVisible: true });
  };

  render() {
    const { buildProducts, buildPurchaserInfo, buildSummary, order, locale, couriers, motives } = this.props;
    const {
      deliveryMethod, confirmQempoDelivered, courier, trackingNumber, nextStep, invoice, reportModalVisible,
    } = this.state;
    const address: AddressIntl = getQempoAddress(locale.domainCountry || 'PE');
    const shippingAddress: Address = order.shippingAddress;
    const { address1, address2, admin1, admin2, firstName, idNumber, lastName, phone } = shippingAddress;
    const courierOptions: Option[] = couriers ? couriers.map((courier: Courier) => ({
      value: courier.id,
      label: courier.name,
    })) : [];
    const options = motives ? motives.traveler.map((motive: Motive) => ({
      value: motive.name,
      label: motive.translations[locale.locale.toUpperCase()],
    })) : [];
    const isButtonDisabled = !deliveryMethod.value ||
      (deliveryMethod.value === 'qempo' && !confirmQempoDelivered && nextStep) ||
      (deliveryMethod.value === 'courier' && (!courier.value || !trackingNumber.value || !invoice.valueURI) && nextStep);
    return (
      <Fragment>
        <main className={commonStyle.mainWrap}>
          <section className={classNames(commonStyle.wrap, commonStyle.leftWrap)}>
            {/*Choose delivery method*/}
            {!nextStep &&
            <section className={classNames(style.box, boxStyle.box)}>
              {order.deliveryCountry.code === 'PE' && <Fragment>
                <FormattedMessage id="order.sale.deliver.options.title">{
                  txt => <h2 className={boxStyle.boxTitle}>{txt}</h2>
                }</FormattedMessage>
                <RadioButton name="deliveryMethod"
                             value="qempo"
                             selected={deliveryMethod.value}
                             handleChange={this.handleChangeDeliveryMethod}
                             color="orange">
                  <FormattedMessage id="order.sale.deliver.option.qempo">{
                    txt => <span>{txt}</span>
                  }</FormattedMessage>
                </RadioButton>
              </Fragment>}
              <FormattedMessage id="order.sale.deliver.option.qempo.info">{
                txt => <p className={style.methodInfo}>{txt}</p>
              }</FormattedMessage>
              <RadioButton name="deliveryMethod"
                           value="courier"
                           selected={deliveryMethod.value}
                           handleChange={this.handleChangeDeliveryMethod}
                           color="orange">
                <FormattedMessage id="order.sale.deliver.option.courier">{
                  txt => <span>{txt}</span>
                }</FormattedMessage>
              </RadioButton>
              <FormattedMessage id="order.sale.deliver.option.courier.info">{
                txt => <p className={style.methodInfo}>{txt}</p>
              }</FormattedMessage>
            </section>}
            {/*Confirm delivery*/}
            <div className={style.top}>
              {nextStep && deliveryMethod.value === 'qempo' &&
              <section className={classNames(style.box, boxStyle.box)}>
                <section className={style.topWrap}>
                  <FormattedMessage id="order.sale.deliver.qempo.title">{
                    txt => <h2 className={boxStyle.boxTitle}>{txt}</h2>
                  }</FormattedMessage>
                  <FormattedMessage id="order.sale.deliver.qempo.subtitle">{
                    txt => <p className={classNames(boxStyle.subtitle, style.subtitle)}>{txt}</p>
                  }</FormattedMessage>
                  <FormattedMessage id="order.sale.deliver.qempo.delivered">{
                    txt => <Checkbox name="confirmQempoDelivered"
                                     value="confirmQempoDelivered"
                                     isChecked={confirmQempoDelivered}
                                     handleChange={this.handleChangeConfirm}
                                     color="orange"
                                     className={style.confirm}>{txt}</Checkbox>
                  }</FormattedMessage>
                </section>
                <hr className={classNames(style.hr, { [style.qempo]: deliveryMethod.value === 'qempo' })}/>
                <FormattedMessage id="order.sale.deliver.qempo.info.title">{
                  txt => <h2 className={boxStyle.boxTitle}>{txt}</h2>
                }</FormattedMessage>
                <form className={formStyle.formWrap}>
                  <section className={classNames(formStyle.row, formStyle.inputRow)}>
                    <FormattedMessage id={address.address.label}>{
                      txt => <label className={formStyle.label}>{txt}</label>
                    }</FormattedMessage>
                    <FormattedMessage id="qempo.address.PE.covid.value.1">{
                      txt1 => <FormattedMessage id="qempo.address.PE.covid.value.2">{
                        txt2 => <p>
                          {txt1}&nbsp;
                          <a
                            className={style.mail}
                            href="mailto:hola@qempo.com?Subject=Entrega%20de%20productos."
                          >
                            hola@qempo.com
                          </a>&nbsp;
                          {txt2}
                        </p> 
                      }</FormattedMessage>
                    }</FormattedMessage>
                  </section>
                  <section className={classNames(formStyle.row, formStyle.dualWrap, formStyle.rowNoMargin)}>
                    <article className={formStyle.col}>
                      <FormattedMessage id={address.businessHours.label}>{
                        txt => <label className={formStyle.label}>{txt}</label>
                      }</FormattedMessage>
                      <FormattedMessage id={address.businessHours.value}>{
                        txt => <p>{txt}</p>
                      }</FormattedMessage>
                    </article>
                    <article className={formStyle.col}>
                      <FormattedMessage id={address.phone.label}>{
                        txt => <label className={formStyle.label}>{txt}</label>
                      }</FormattedMessage>
                      <FormattedMessage id={address.phone.value}>{
                        txt => <p>{txt}</p>
                      }</FormattedMessage>
                    </article>
                  </section>
                </form>
              </section>}
              {/*Courier*/}
              {nextStep && deliveryMethod.value === 'courier' &&
              <section className={classNames(style.box, boxStyle.box)}>
                <section className={style.topWrap}>
                  <FormattedMessage id="order.sale.deliver.courier.title">{
                    txt => <h2 className={boxStyle.boxTitle}>{txt}</h2>
                  }</FormattedMessage>
                  <FormattedMessage id="order.sale.deliver.courier.subtitle">{
                    txt => <p className={classNames(boxStyle.subtitle, style.subtitle)}>{txt}</p>
                  }</FormattedMessage>
                  <form className={formStyle.formWrap}>
                    <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.dualWrap,
                      formStyle.responsiveMedium)}>
                      <article className={formStyle.col}>
                        <FormattedMessage id="order.sale.deliver.courier.choose.label">{
                          txt => <label className={formStyle.label}>{txt}</label>
                        }</FormattedMessage>
                        <FormattedMessage id="order.sale.deliver.courier.choose.select">{
                          txt => <Select options={courierOptions}
                                         className={classNames({
                                           'light-blue': courier.value,
                                         })}
                                         placeholder={txt}
                                         value={courier.value}
                                         handleChange={this.handleChangeCourier}/>
                        }</FormattedMessage>
                      </article>
                      <article className={formStyle.col}>
                        <FormattedMessage id="order.sale.deliver.courier.tracking.label">{
                          txt => <label className={formStyle.label}>{txt}</label>
                        }</FormattedMessage>
                        <input type="text"
                               name="trackingNumber"
                               className={classNames(inputStyle.input, { [inputStyle.lightBlue]: trackingNumber.value })}
                               value={trackingNumber.value}
                               onChange={this.handleChangeTrackingNumber}/>
                      </article>
                    </section>
                    <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.rowNoMargin)}>
                      <FormattedMessage id="order.sale.deliver.courier.tracking.label">{
                        txt => <label className={formStyle.label}>{txt}</label>
                      }</FormattedMessage>
                      <UploadBox error={invoice.error || false}
                                 handleChangeFile={(files: File[]) => this.handleChangeFile(files)}
                                 handleRejectedFile={(files: File[]) => this.handleRejectedFile(files)}
                                 hasInvoice={!!invoice.valueURI}
                                 uploadedIntl="order.sale.deliver.courier.invoice.ready"
                                 uploadIntl="order.sale.deliver.courier.invoice.upload"/>
                    </section>
                  </form>
                </section>
                <hr className={style.hr}/>
                <FormattedMessage id="order.sale.deliver.courier.info.title">{
                  txt => <h2 className={boxStyle.boxTitle}>{txt}</h2>
                }</FormattedMessage>
                <form className={formStyle.formWrap}>
                  <section className={classNames(formStyle.row, formStyle.inputRow)}>
                    <FormattedMessage id={address.address.label}>{
                      txt => <label className={formStyle.label}>{txt}</label>
                    }</FormattedMessage>
                    <p>{address1}, {address2 ? `${address2},` : ''} {admin2.name} - {admin1.name}</p>
                  </section>
                  <section className={classNames(formStyle.row, formStyle.dualWrap)}>
                    <article className={formStyle.col}>
                      <FormattedMessage id="address.firstName.label">{
                        txt => <label className={formStyle.label}>{txt}</label>
                      }</FormattedMessage>
                      <p>{firstName} {lastName}</p>
                    </article>
                    <article className={formStyle.col}>
                      <FormattedMessage id="address.idNumber.label">{
                        txt => <label className={formStyle.label}>{txt}</label>
                      }</FormattedMessage>
                      <p>{idNumber}</p>
                    </article>
                  </section>
                  <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.rowNoMargin)}>
                    <FormattedMessage id="address.phone.label">{
                      txt => <label className={formStyle.label}>{txt}</label>
                    }</FormattedMessage>
                    <p>{phone}</p>
                  </section>
                </form>
              </section>}
            </div>
            {buildProducts()}
          </section>
          <section className={classNames(commonStyle.wrap, commonStyle.rightWrap)}>
            {buildPurchaserInfo()}
            {buildSummary()}
            <FormattedMessage id="order.sale.deliver.continue.button">{
              txt => <button onClick={this.handleNext}
                             className={classNames(style.button, buttonStyle.btn, buttonStyle.orange)}
                             disabled={isButtonDisabled}>
                {txt}</button>
            }</FormattedMessage>
            {/*nextStep && <FormattedMessage id="order.sale.deliver.back.button">{
              txt => <button onClick={this.handleBack}
                             className={classNames(style.button, buttonStyle.btn, buttonStyle.darkGrey)}>
                {txt}</button>
            }</FormattedMessage>*/}
            <FormattedMessage id="order.sale.deliver.report.button">{
              txt => <button className={classNames(style.button, buttonStyle.btn, buttonStyle.redInverse)}
                             onClick={this.handleOpenReportModal}>{txt}</button>
            }</FormattedMessage>
          </section>
          <section className={style.buttonsWrap}>
            <FloatingBottom offSetY={nextStep ? 216 : 152}>
              <FormattedMessage id="order.sale.deliver.continue.button">{
                txt => <button onClick={this.handleNext}
                               className={classNames(buttonStyle.btn, buttonStyle.orange)}
                               disabled={isButtonDisabled}>
                  {txt}</button>
              }</FormattedMessage>
              {/*nextStep && <FormattedMessage id="order.sale.deliver.back.button">{
                txt => <button onClick={this.handleBack}
                               className={classNames(buttonStyle.btn, buttonStyle.darkGrey, style.below)}>
                  {txt}</button>
              }</FormattedMessage>*/}
              <FormattedMessage id="order.sale.deliver.report.button">{
                txt => <button className={classNames(buttonStyle.btn, buttonStyle.redInverse, style.below)}
                               onClick={this.handleOpenReportModal}>{txt}</button>
              }</FormattedMessage>
            </FloatingBottom>
          </section>
        </main>
        <ReportModalContainer color="orange"
                              reportType="TRAVELER_TRACKING"
                              isOpen={reportModalVisible}
                              handleClose={this.handleCloseReportModal}
                              isDiy={order.isDiy}
                              id={order.id}
                              options={options}
                              successIntl="modal.report.traveler.success.text"/>
      </Fragment>
    );
  }
}

Deliver.propTypes = {
  buildProducts: PropTypes.func.isRequired,
  buildPurchaserInfo: PropTypes.func.isRequired,
  buildSummary: PropTypes.func.isRequired,
  order: PropTypes.object.isRequired,
  locale: PropTypes.object.isRequired,
  localValues: PropTypes.object.isRequired,
  postProgressCourier: PropTypes.func.isRequired,
  postProgressQempo: PropTypes.func.isRequired,
  fetchCouriers: PropTypes.func.isRequired,
  couriers: PropTypes.array,
};



// WEBPACK FOOTER //
// ./src/modules/order/Sale/deliver/components/Deliver.jsx